import React from "react";
import styled from "styled-components";

interface GetFlagParams {
    position?: string;
    flagCode: string;
    flagType?: string;
    className?: string;
    props?: React.HTMLAttributes<HTMLSpanElement>;
}

interface FlagProps extends React.HTMLAttributes<HTMLDivElement> {
    countryCode: string;
    primaryFlag?: string;
    secondaryFlag?: string;
    className?: string;
    position?: string;
}

const DualFlags = styled.div`
    vertical-align: top;
    display: inline-flex;
    width: 1.4em;
    padding: 0;
    margin: 0;
`;

const PrimaryFlag = styled.div`
    display: inline-block;
    width: 0.7em;
    vertical-align: inherit;

    > span {
        left: -0.1em;
        vertical-align: inherit;
        height: 100%;
    }
`;

const SecondaryFlag = styled.div`
    display: inline-block;
    width: 0.7em;
    vertical-align: inherit;

    > span {
        right: 0.6em;
        vertical-align: inherit;
        height: 100%;
        margin-left: 20px;
    }
`;

const getFlag = ({
    position = "right",
    flagCode,
    flagType,
    className = "",
    props = {},
}: GetFlagParams) => {
    if (flagCode === "gb-nir" || flagCode === "nir") return null;

    const ariaLabel = flagType
        ? `${flagType} Country flag ${flagCode}`
        : `Country flag ${flagCode}`;

    return (
        <span
            {...props}
            data-test-id={`${position}-test-flag-id-${flagCode}`}
            className={`${
                flagCode !== "gb-nir" && flagCode !== "nir" ? `flag-icon flag-icon-${flagCode}` : ""
            } ${className || ""}`.trim()}
            role="img"
            aria-label={ariaLabel}
        />
    );
};

export const Flag: React.FC<FlagProps> = ({
    countryCode,
    primaryFlag,
    secondaryFlag,
    className,
    position,
    ...props
}) => {
    if (typeof countryCode !== "string" && !primaryFlag && !secondaryFlag) return null;

    if (primaryFlag && secondaryFlag) {
        // Dual Citizenship
        return (
            <DualFlags className={className} {...props}>
                <PrimaryFlag>
                    {getFlag({
                        position,
                        flagCode: primaryFlag.toLowerCase(),
                        flagType: "Primary",
                        props,
                    })}
                </PrimaryFlag>
                <SecondaryFlag>
                    {getFlag({
                        position,
                        flagCode: secondaryFlag.toLowerCase(),
                        flagType: "Secondary",
                        props,
                    })}
                </SecondaryFlag>
            </DualFlags>
        );
    } else if (primaryFlag) {
        // Single citizenship
        return getFlag({
            position,
            flagCode: primaryFlag.toLowerCase(),
            className,
            props,
            flagType: "Primary",
        });
    } else if (secondaryFlag) {
        // Single citizenship
        return getFlag({
            position,
            flagCode: secondaryFlag.toLowerCase(),
            className,
            props,
            flagType: "Secondary",
        });
    }

    // Backwards compatibility
    return getFlag({
        position,
        flagCode: countryCode.toLowerCase(),
        className,
        props,
    });
};
